import React, { useCallback, useEffect, useRef } from "react";
import { Check, X } from "lucide-react";
import { NotificationEmpty } from "./NotificationEmpty";
import { NotificationSkeleton } from "./NotificationSkeleton";

export interface NotificationFilter {
  key: string;
  label: string;
}

export interface NotificationPanelProps<T extends { id: number | string }> {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  unreadCount?: number;
  notifications: T[];
  isLoading?: boolean;
  isLoadingMore?: boolean;
  filters?: NotificationFilter[];
  selectedFilter?: string;
  onFilterChange?: (filterKey: string) => void;
  onMarkAllAsRead?: () => void;
  onLoadMore?: () => void;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
  canPreviousPage?: boolean;
  canNextPage?: boolean;
  renderNotification: (notification: T) => React.ReactNode;
  loadMoreLabel?: string;
  previousLabel?: string;
  nextLabel?: string;
  loadingLabel?: string;
  pageInfo?: {
    current: number;
    total: number;
  };
  pageInfoText?: string;
  emptyState?: React.ReactNode;
}

export const NotificationPanel = <T extends { id: number | string }>({
  isOpen,
  onClose,
  title = "Notifications",
  unreadCount = 0,
  notifications,
  isLoading = false,
  isLoadingMore = false,
  filters = [],
  selectedFilter = "",
  onFilterChange,
  onMarkAllAsRead,
  onLoadMore,
  onPreviousPage,
  onNextPage,
  canPreviousPage,
  canNextPage,
  renderNotification,
  loadMoreLabel = "Load more",
  previousLabel = "Previous",
  nextLabel = "Next",
  loadingLabel = "Loading...",
  pageInfo,
  pageInfoText,
  emptyState,
}: NotificationPanelProps<T>) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, handleClickOutside]);

  const handleScroll = useCallback(() => {
    if (!scrollableRef.current || !onLoadMore || !pageInfo) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollableRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

    if (isNearBottom && !isLoadingMore && pageInfo.current < pageInfo.total) {
      onLoadMore();
    }
  }, [isLoadingMore, onLoadMore, pageInfo]);

  if (!isOpen) return null;

  const hasNotifications = notifications.length > 0;
  const showLoadMoreButton =
    typeof onLoadMore === "function" &&
    (pageInfo ? pageInfo.current < pageInfo.total : true);

  return (
    <>
      <div
        className="fixed inset-0 z-40 opacity-0 pointer-events-none"
        onClick={onClose}
      />

      <div
        ref={panelRef}
        className="absolute right-0 top-full mt-3 w-96 max-w-[calc(100vw-1rem)] bg-white rounded-xl shadow-lg shadow-black/10 border border-gray-100 z-50 flex flex-col max-h-[600px] overflow-hidden overflow-x-hidden backdrop-blur-sm transition-all duration-200 ease-out animate-panel-enter"
      >
        <div className="sticky top-0 z-20 shrink-0 bg-white">
          <div className="px-4 py-3 flex items-center justify-between bg-gradient-to-r from-white to-gray-50/50 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900 tracking-tight">
              {title}
            </h2>

            <div className="flex items-center gap-2">
              {unreadCount && unreadCount > 0 && onMarkAllAsRead && (
                <button
                  onClick={onMarkAllAsRead}
                  className="flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded-md transition-all duration-150"
                  title="Mark all as read"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
              )}

              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-md transition-all duration-150"
                aria-label="Close notifications"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {filters.length > 0 && onFilterChange && (
            <div className="px-3 py-2 bg-gray-50/50 border-b border-gray-100 overflow-x-hidden">
              <div className="flex flex-wrap gap-2 min-w-0">
                {filters.map((filter) => (
                  <button
                    key={filter.key}
                    onClick={() => onFilterChange(filter.key)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 whitespace-nowrap ${selectedFilter === filter.key
                      ? "bg-primary text-white shadow-sm shadow-primary/20"
                      : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                      }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div
          ref={scrollableRef}
          onScroll={handleScroll}
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden bg-white pr-1 scroll-smooth"
          style={{ scrollBehavior: "smooth" }}
        >
          {isLoading && !hasNotifications ? (
            <NotificationSkeleton count={3} />
          ) : !hasNotifications ? (
            emptyState || <NotificationEmpty />
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => renderNotification(notification))}
              {isLoadingMore && <NotificationSkeleton count={2} />}
            </div>
          )}
        </div>

        {hasNotifications && (
          <div className="bg-gradient-to-t from-gray-50 to-white border-t border-gray-100">
            {typeof onPreviousPage === "function" || typeof onNextPage === "function" ? (
              <div className="px-4 py-3 flex items-center justify-between gap-3">
                <button
                  onClick={onPreviousPage}
                  disabled={!canPreviousPage}
                  className="flex-1 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                >
                  {pageInfo ? pageInfo.current > 1 ? "Previous" : "Previous" : previousLabel || "Previous"}
                </button>

                <div className="text-xs text-gray-500 text-center min-w-[90px]">
                  {pageInfoText || (pageInfo ? `Page ${pageInfo.current} of ${pageInfo.total}` : null)}
                </div>

                <button
                  onClick={onNextPage}
                  disabled={!canNextPage}
                  className="flex-1 px-3 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                >
                  {nextLabel || "Next"}
                </button>
              </div>
            ) : (
              showLoadMoreButton && (
                <button
                  onClick={onLoadMore}
                  disabled={isLoadingMore}
                  className="w-full px-4 py-2.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 flex items-center justify-center"
                >
                  {isLoadingMore ? loadingLabel : loadMoreLabel}
                </button>
              )
            )}

            {pageInfo && !(typeof onPreviousPage === "function" || typeof onNextPage === "function") && (
              <div className="px-4 py-2 text-xs text-gray-400 text-center border-t border-gray-100 bg-white">
                {pageInfoText || `Page ${pageInfo.current} of ${pageInfo.total}`}
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes panel-enter {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-panel-enter {
          animation: panel-enter 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .scroll-smooth {
          scroll-behavior: smooth;
        }
      `}</style>
    </>
  );
};
